import { Button, ButtonGroup, Heading, InlineFeedback, Modal, Text } from '@zillow/constellation-10';
import { useCallback } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import type { CommuteDestination } from './types';
import { formatCommuteDistanceMiles } from './commuteDistance';

type SavedCommuteDistancePromptProps = {
  destination: CommuteDestination;
  distanceMiles: number;
  onUseAnyway: () => void;
  onChangeDestination: () => void;
};

type CommuteDistanceConfirmationModalProps = {
  destination: CommuteDestination | null;
  distanceMiles: number | null;
  isOpen: boolean;
  onUseAnyway: () => void;
  onChooseAnother: () => void;
};

export function SavedCommuteDistancePrompt({
  destination,
  distanceMiles,
  onUseAnyway,
  onChangeDestination,
}: SavedCommuteDistancePromptProps) {
  const t = useTranslations('hdp');
  const locale = useLocale();
  const distance = formatCommuteDistanceMiles(distanceMiles, locale);

  return (
    <div>
      <InlineFeedback tone="info">
        {t('savedCommuteDistance', {
          name: destination.name,
          distance,
        })}
      </InlineFeedback>
      <ButtonGroup
        aria-label={t('savedCommuteActions')}
        size="sm"
        orientation={{ base: 'vertical', lg: 'horizontal' }}
        css={{ marginBlockStart: 'tighter' }}
      >
        <Button tone="brand" emphasis="outlined" onClick={onUseAnyway}>
          {t('useAnyway')}
        </Button>
        <Button tone="neutral" emphasis="bare" onClick={onChangeDestination}>
          {t('changeDestination')}
        </Button>
      </ButtonGroup>
    </div>
  );
}

export function CommuteDistanceConfirmationModal({
  destination,
  distanceMiles,
  isOpen,
  onUseAnyway,
  onChooseAnother,
}: CommuteDistanceConfirmationModalProps) {
  const t = useTranslations('hdp');
  const locale = useLocale();
  const distance = distanceMiles === null ? '' : formatCommuteDistanceMiles(distanceMiles, locale);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) onChooseAnother();
    },
    [onChooseAnother],
  );

  return (
    <Modal.Root open={isOpen} onOpenChange={handleOpenChange} size={{ base: 'fullScreen', lg: 'sm' }}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content aria-label={t('useCommuteDestinationQuestion')}>
            <Modal.Header>
              <Heading level={2}>{t('useCommuteDestinationQuestion')}</Heading>
            </Modal.Header>
            <Modal.Body>
              {destination && (
                <Text asChild textStyle="body">
                  <p>
                    {t('commuteDestinationDistance', {
                      name: destination.name,
                      distance,
                    })}
                  </p>
                </Text>
              )}
            </Modal.Body>
            <Modal.Footer>
              <ButtonGroup
                aria-label={t('commuteDistanceConfirmationActions')}
                size="sm"
                orientation={{ base: 'vertical', lg: 'horizontal' }}
              >
                <Button tone="brand" emphasis="filled" onClick={onUseAnyway}>
                  {t('useAnyway')}
                </Button>
                <Button tone="neutral" emphasis="outlined" onClick={onChooseAnother}>
                  {t('chooseAnother')}
                </Button>
              </ButtonGroup>
            </Modal.Footer>
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}
